import { useState, type FormEvent, type ReactNode } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { LogoMarque } from '../marque/LogoMarque';
import { ErreurApi } from '../lib/api';
import { detailsRefus } from '../lib/erreurs-api';
import {
  useDeposerPreinscription,
  useFormulairePreinscription,
  type DepotPreinscription,
} from '../lib/usePreinscriptions';
import { useMarque } from '../marque/useMarque';
import { useTitrePage } from '../lib/useTitrePage';

const VIDE: DepotPreinscription = {
  nom: '',
  prenom: '',
  dateNaissance: '',
  email: '',
  telephone: '',
  mode: '',
  siteCode: '',
  formationCode: '',
  message: '',
  consentement: false,
  siteWeb: '',
};

/** Une précision sous chaque parcours : le candidat ne parle ni de CFA ni d'OF. */
const PRECISION_PARCOURS: Record<string, string> = {
  CFA: 'En alternance, avec un contrat dans une entreprise.',
  OF: 'Une session de formation, financée par France Travail, le CPF ou votre employeur.',
};

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-codialis-navy focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint';

function aujourdHuiReunion(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Indian/Reunion' });
}

/**
 * Préinscription en ligne (TF-429), sans compte : le lien se partage à la main.
 * Le candidat dit qui il est, où et quoi ; la direction étudie sa demande et
 * lui répond par e-mail. Ce qui est refusé à la saisie est nommé champ par
 * champ, tel que l'API le renvoie.
 */
export function PreinscriptionPublicPage(): JSX.Element {
  useTitrePage('Préinscription');
  const formulaire = useFormulairePreinscription();
  const { marque } = useMarque();
  const deposer = useDeposerPreinscription();
  const [saisie, setSaisie] = useState<DepotPreinscription>(VIDE);

  const changer = <C extends keyof DepotPreinscription>(cle: C, valeur: DepotPreinscription[C]): void =>
    setSaisie((avant) => ({ ...avant, [cle]: valeur }));

  // Le premier refus d'un champ est le plus parlant : « obligatoire » avant « invalide ».
  const refus = new Map<string, string>();
  for (const detail of detailsRefus(deposer.error)) {
    if (!refus.has(detail.champ)) refus.set(detail.champ, detail.message);
  }
  const erreurGenerale =
    deposer.error && refus.size === 0
      ? deposer.error instanceof ErreurApi
        ? deposer.error.message
        : 'La préinscription n’a pas pu être envoyée. Réessayez dans quelques instants.'
      : null;

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    deposer.mutate(saisie);
  };

  return (
    <div className="login-bg flex min-h-screen items-start justify-center p-4 sm:items-center sm:p-6">
      <div className="w-full max-w-2xl rounded-3xl bg-white p-4 shadow-2xl sm:p-8 md:p-10">
        <LogoMarque className="mb-8 h-9 w-auto" />

        {deposer.isSuccess ? (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-codialis-teal" aria-hidden />
            <h1 className="font-display text-2xl font-bold text-codialis-navy">Préinscription envoyée</h1>
            <p className="text-sm text-codialis-gray">{deposer.data.message}</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-codialis-navy">Préinscription</h1>
            <p className="mt-1 text-sm text-codialis-gray">
              Remplissez ce formulaire pour vous préinscrire à une formation de {marque.nom}. Votre demande est étudiée
              par l’équipe, qui vous répond par e-mail.
            </p>

            {formulaire.isLoading && <p className="mt-6 text-sm text-codialis-gray">Chargement du formulaire…</p>}
            {formulaire.error && (
              <p role="alert" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
                {formulaire.error instanceof ErreurApi
                  ? formulaire.error.message
                  : 'Le formulaire n’a pas pu être chargé. Réessayez dans quelques instants.'}
              </p>
            )}

            {formulaire.data && (
              <form onSubmit={envoyer} noValidate className="relative mt-6 space-y-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Champ id="prenom" libelle="Prénom" erreur={refus.get('prenom')}>
                    <input
                      id="prenom"
                      required
                      autoComplete="given-name"
                      maxLength={80}
                      value={saisie.prenom}
                      onChange={(evenement) => changer('prenom', evenement.target.value)}
                      className={CLASSE_CHAMP}
                    />
                  </Champ>
                  <Champ id="nom" libelle="Nom" erreur={refus.get('nom')}>
                    <input
                      id="nom"
                      required
                      autoComplete="family-name"
                      maxLength={80}
                      value={saisie.nom}
                      onChange={(evenement) => changer('nom', evenement.target.value)}
                      className={CLASSE_CHAMP}
                    />
                  </Champ>
                  <Champ id="dateNaissance" libelle="Date de naissance" erreur={refus.get('dateNaissance')}>
                    <input
                      id="dateNaissance"
                      type="date"
                      required
                      autoComplete="bday"
                      min="1940-01-01"
                      max={aujourdHuiReunion()}
                      value={saisie.dateNaissance}
                      onChange={(evenement) => changer('dateNaissance', evenement.target.value)}
                      className={CLASSE_CHAMP}
                    />
                  </Champ>
                  <Champ id="telephone" libelle="Téléphone" erreur={refus.get('telephone')}>
                    <input
                      id="telephone"
                      type="tel"
                      inputMode="tel"
                      required
                      autoComplete="tel"
                      maxLength={30}
                      placeholder="0692 12 34 56"
                      value={saisie.telephone}
                      onChange={(evenement) => changer('telephone', evenement.target.value)}
                      className={CLASSE_CHAMP}
                    />
                  </Champ>
                  <div className="sm:col-span-2">
                    <Champ id="email" libelle="E-mail" erreur={refus.get('email')}>
                      <input
                        id="email"
                        type="email"
                        inputMode="email"
                        required
                        autoComplete="email"
                        maxLength={160}
                        value={saisie.email}
                        onChange={(evenement) => changer('email', evenement.target.value)}
                        className={CLASSE_CHAMP}
                      />
                    </Champ>
                  </div>
                </div>

                <Choix
                  legende="Antenne"
                  erreur={refus.get('siteCode')}
                  nom="antenne"
                  valeur={saisie.siteCode}
                  onChange={(valeur) => changer('siteCode', valeur)}
                  options={formulaire.data.antennes.map((antenne) => ({
                    valeur: antenne.code,
                    libelle: antenne.nom,
                    precision: antenne.ville,
                  }))}
                />

                <Choix
                  legende="Parcours"
                  erreur={refus.get('mode')}
                  nom="parcours"
                  valeur={saisie.mode}
                  onChange={(valeur) => changer('mode', valeur as DepotPreinscription['mode'])}
                  options={formulaire.data.parcours.map((parcours) => ({
                    valeur: parcours.valeur,
                    libelle: parcours.libelle,
                    precision: PRECISION_PARCOURS[parcours.valeur] ?? null,
                  }))}
                />

                <Champ id="formation" libelle="Formation souhaitée" facultatif erreur={refus.get('formationCode')}>
                  <select
                    id="formation"
                    value={saisie.formationCode}
                    onChange={(evenement) => changer('formationCode', evenement.target.value)}
                    className={CLASSE_CHAMP}
                  >
                    <option value="">Je ne sais pas encore</option>
                    {formulaire.data.formations.map((formation) => (
                      <option key={formation.code} value={formation.code}>
                        {formation.titre}
                      </option>
                    ))}
                  </select>
                </Champ>

                <Champ id="message" libelle="Message" facultatif erreur={refus.get('message')}>
                  <textarea
                    id="message"
                    rows={3}
                    maxLength={1000}
                    placeholder="Vos disponibilités, une question…"
                    value={saisie.message}
                    onChange={(evenement) => changer('message', evenement.target.value)}
                    className={`${CLASSE_CHAMP} resize-y`}
                  />
                </Champ>

                {/* Champ piège : invisible et hors du parcours au clavier, seul un robot le remplit. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute left-0 top-0 h-0 w-0 overflow-hidden opacity-0"
                >
                  <label htmlFor="site-web">Site web</label>
                  <input
                    id="site-web"
                    tabIndex={-1}
                    autoComplete="off"
                    value={saisie.siteWeb}
                    onChange={(evenement) => changer('siteWeb', evenement.target.value)}
                  />
                </div>

                <div>
                  <label className="flex min-h-[44px] items-start gap-3 text-sm text-codialis-gray">
                    <input
                      type="checkbox"
                      checked={saisie.consentement}
                      onChange={(evenement) => changer('consentement', evenement.target.checked)}
                      aria-describedby={refus.get('consentement') ? 'consentement-erreur' : undefined}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded accent-codialis-mint"
                    />
                    <span>
                      J’accepte que {formulaire.data.organisme} utilise ces informations pour étudier ma préinscription
                      et me recontacter. Elles ne sont transmises à personne d’autre.
                    </span>
                  </label>
                  {refus.get('consentement') && (
                    <p id="consentement-erreur" role="alert" className="mt-1 text-xs font-medium text-red-600">
                      {refus.get('consentement')}
                    </p>
                  )}
                </div>

                {erreurGenerale && (
                  <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
                    {erreurGenerale}
                  </p>
                )}
                {refus.size > 0 && (
                  <p role="alert" className="text-sm font-medium text-red-600">
                    Vérifiez les champs signalés.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={deposer.isPending}
                  className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-codialis-navy px-6 py-3 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deposer.isPending ? 'Envoi en cours…' : 'Envoyer ma préinscription'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Champ({
  id,
  libelle,
  facultatif = false,
  erreur,
  children,
}: {
  id: string;
  libelle: string;
  facultatif?: boolean;
  erreur: string | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy">
        {libelle}
        {facultatif && (
          <span className="font-normal normal-case tracking-normal text-codialis-gray"> · facultatif</span>
        )}
      </label>
      {children}
      {erreur && (
        <p role="alert" className="mt-1 text-xs font-medium text-red-600">
          {erreur}
        </p>
      )}
    </div>
  );
}

function Choix({
  legende,
  nom,
  valeur,
  onChange,
  options,
  erreur,
}: {
  legende: string;
  nom: string;
  valeur: string;
  onChange: (valeur: string) => void;
  options: { valeur: string; libelle: string; precision: string | null }[];
  erreur: string | undefined;
}): JSX.Element {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-codialis-navy">{legende}</legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const choisi = option.valeur === valeur;
          return (
            <label
              key={option.valeur}
              className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border-2 p-3 transition ${
                choisi ? 'border-codialis-mint bg-codialis-mint/5' : 'border-gray-100 hover:border-gray-200'
              }`}
            >
              <input
                type="radio"
                name={nom}
                value={option.valeur}
                checked={choisi}
                onChange={() => onChange(option.valeur)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-codialis-mint"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-codialis-navy">{option.libelle}</span>
                {option.precision && <span className="block text-xs text-codialis-gray">{option.precision}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {erreur && (
        <p role="alert" className="mt-1 text-xs font-medium text-red-600">
          {erreur}
        </p>
      )}
    </fieldset>
  );
}
